"use client"

import { useState } from "react"
import CreateText from "./CreateText"
import { auth, database } from "../../config/firebase"
import {
  update as updateFirebase,
  ref,
  set,
  push as pushToDatabase,
  serverTimestamp as serverTimestampFunction,
} from "firebase/database"
import { UserAuth } from "../../context/AuthContext"
import useFetch from "../../hooks/useFetch"
import Loading from "../../components/Loading"
import Swal from "sweetalert2"
import { redirect, useNavigate, useParams } from "react-router-dom"
import useFetchText from "../../hooks/useFetchText"
import { useEffect } from "react"
import { useTranslations } from "../../translations"
import { useEntitlement } from "../../hooks/useEntitlement"
import AiPaywall from "../../components/protected/AiPaywall"

const PAYWALL_ENFORCE = process.env.REACT_APP_AI_PAYWALL_ENFORCE === "true"

const CreateTextContainer = ({ isEdit = false }) => {
  const { user } = UserAuth()
  const entitlement = useEntitlement()
  const [form, setForm] = useState({
    text: "",
    number: 0,
    type: "contextBased",
    topic: "",
    questionType: "-1",
    difficulty: "-1",
    format: "-1",
    levels: "",
    language: "",
  })
  const levelsTexts = ["crear", "evaluar", "analizar", "aplicar", "entender", "recordar"]

  const [loading, setLoading] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const { data: dataUser, loading: isLoading, error: errorUser } = useFetch()
  const [qaPairs, setQAPairs] = useState([])
  const [error, setError] = useState(null)
  const max = 10
  const maxBlooms = 5
  const [checked, setChecked] = useState([])
  const [isChecked, setIsChecked] = useState([])
  const [checkedLevels, setCheckedLevels] = useState([])
  const [isCheckedLevels, setIsCheckedLevels] = useState(new Array(6).fill(false))

  const navigate = useNavigate()
  const { id } = useParams()
  const { data: dataText, loading: loadingText, error: errorText } = useFetchText(id)

  const { t } = useTranslations()

  useEffect(() => {
    handleReset()
  }, [dataText, id])
  useEffect(() => {
    setCheckedLevels([])
    setIsCheckedLevels(new Array(6).fill(false))
    if (dataText && dataText.levels && id) {
      // Inicializa los estados según los datos guardados
      const levelsBlank = []
      const IsCheckedLevels = [...isCheckedLevels]
      console.log(dataText)
      if (typeof dataText.levels === "object" && !Array.isArray(dataText.levels)) {
        dataText.levels = Object.values(dataText.levels)
      }
      dataText.levels.forEach((valor) => {
        const indice = levelsTexts.indexOf(valor)
        if (indice !== -1) {
          levelsBlank[indice] = valor
          IsCheckedLevels[indice] = true
        }
      })

      setCheckedLevels(levelsBlank)
      setIsCheckedLevels(IsCheckedLevels)
    }
  }, [dataText, id])

  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener("mouseenter", Swal.stopTimer)
      toast.addEventListener("mouseleave", Swal.resumeTimer)
    },
  })

  const handleDelete = () => {
    setQAPairs(qaPairs.filter((item2) => !checked.some((item1) => item1 === item2.id)))
    setForm({
      ...form,
      number: qaPairs.filter((item2) => !checked.some((item1) => item1 === item2.id)).length,
    })
    setChecked([])
    setIsChecked(new Array(qaPairs.length).fill(false))
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm({ ...form, [name]: value })
    setQAPairs([])
    // Al cambiar de formato (Abierto ↔ Bloom), limpia los niveles del otro modo
    // para no arrastrar estado que no corresponde al formato elegido.
    if (name === "format") {
      setCheckedLevels([])
      setIsCheckedLevels(new Array(6).fill(false))
    }
  }

  const handleFileTextExtracted = (extractedText) => {
    setForm({ ...form, text: extractedText })
    setQAPairs([])
  }

  const handleCheck = (event, index) => {
    let updatedList = [...checked]
    const updatedCheckboxes = [...isChecked]

    if (event.target.checked) {
      updatedList = [...checked, event.target.value]
      updatedCheckboxes[index] = true
    } else {
      updatedList.splice(checked.indexOf(event.target.value), 1)
      updatedCheckboxes[index] = false
    }
    setChecked(updatedList)
    setIsChecked(updatedCheckboxes)
  }
  const handleCheckLevels = (event, index) => {
    let updatedList = [...checkedLevels]
    const updatedCheckboxes = [...isCheckedLevels]

    // Cambiar la selección de niveles invalida las preguntas ya generadas
    // (evita guardar un resultado que no corresponde a los niveles marcados).
    setQAPairs([])

    if (event.target.checked) {
      updatedList = [...checkedLevels, event.target.value]
      updatedCheckboxes[index] = true
    } else {
      updatedList.splice(checkedLevels.indexOf(event.target.value), 1)
      updatedCheckboxes[index] = false
    }
    setCheckedLevels(updatedList)
    setIsCheckedLevels(updatedCheckboxes)
  }
  const handleReset = () => {
    setForm({
      text: id ? dataText.text : "",
      number: id ? dataText.numberOfQuestions : 0,
      type: id ? dataText.type : "",
      questionType: id ? dataText.questionType : "-1",
      difficulty: id ? dataText.difficulty : "-1",
      format: id ? dataText.format : "-1",
      language: id ? dataText.language : "",
    })
    setQAPairs(id ? dataText.questions : [])
    // "Borrar" en modo crear debe limpiar también los niveles de Bloom
    // (estado separado del form). En edición se restauran desde el efecto de carga.
    if (!id) {
      setCheckedLevels([])
      setIsCheckedLevels(new Array(6).fill(false))
    }
  }

  const update = async () => {
    try {
      setLoading(true)
      const textRef = ref(database, `users/${user.uid}/texts/${id}`)
      await updateFirebase(textRef, {
        numberOfQuestions: Number.parseInt(form.number, 10),
        text: form.text,
        questions: qaPairs,
        difficulty: form.difficulty,
        questionType: form.format === "open" ? form.questionType : "MCQ",
        format: form.format,
        levels: checkedLevels,
        language: form.language,
      })
    } catch (error) {
      Toast.fire({
        icon: "error",
        title: t("unexpectedError"),
      }).then(() => {
        navigate("/actions/create-text")
      })
    } finally {
      handleReset()
      setLoading(false)
      Toast.fire({
        icon: "success",
        title: t("updatedSuccessfully"),
      }).then(() => {
        navigate("/actions/list-texts")
      })
    }
  }

  const save = async () => {
    try {
      setLoading(true)

      const textRef = pushToDatabase(ref(database, `users/${user.uid}/texts`))

      const newTextKey = textRef.key

      const newText = {
        id: newTextKey,
        userId: user.uid,
        userAppId: dataUser?.id || user.uid,
        numberOfQuestions: Number.parseInt(form.number, 10),
        text: form.text,
        questions: qaPairs,
        questionType: form.format === "open" ? form.questionType : "MCQ",
        difficulty: form.difficulty,
        format: form.format,
        levels: (checkedLevels || []).filter(Boolean),
        language: form.language,
        timestamp: serverTimestampFunction(),
      }
      // Firebase set() rechaza cualquier `undefined` (p.ej. si la IA omite un campo
      // como "answer"); saneamos el objeto para no perder el guardado.
      const clean = JSON.parse(JSON.stringify(newText))
      clean.timestamp = serverTimestampFunction()
      await set(textRef, clean)

      // Éxito real → recién ahora navegamos a la lista.
      handleReset()
      setLoading(false)
      Toast.fire({
        icon: "success",
        title: t("savedSuccessfully"),
      }).then(() => {
        navigate("/actions/list-texts")
      })
    } catch (error) {
      setLoading(false)
      Toast.fire({
        icon: "error",
        title: t("unexpectedError"),
        text: error?.message,
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!id) {
      if (form.text !== "" && form.number >= 0 && qaPairs.length > 0 && form.language !== "") {
        await save()
      } else {
        Toast.fire({
          icon: "error",
          title: t("completeFieldsAndGenerate"),
        }).then(() => {
          redirect("/actions/create-text")
        })
      }
    } else {
      await update()
    }
  }

  const title = id ? t("editTextContent") : t("createTextForReading")

  const handleGenerateQA = async () => {
    const url = process.env.REACT_APP_QA_API_URL
    const data = {
      content: form.text,
      questionType: form.questionType,
      number: Number.parseInt(form.number, 10) || 0,
      difficulty: form.difficulty,
      language: form.language,
      key: process.env.REACT_APP_QA_API_KEY,
    }
    // Token de Firebase para el gate de entitlement de IA (Fase 0).
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null
    const reqOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify(data),
    }

    try {
      setIsGenerating(true)
      if (form.difficulty === "-1" || form.difficulty === undefined) {
        Toast.fire({
          icon: "error",
          title: t("selectDifficultyFirst"),
        }).then(() => {
          redirect("/actions/create-text")
        })
        return
      }

      if (form.text === "") {
        Toast.fire({
          icon: "error",
          title: t("introduceTextFirst"),
        }).then(() => {
          redirect("/actions/create-text")
        })
        return
      }
      if (form.language === "") {
        Toast.fire({
          icon: "error",
          title: t("selectLanguageFirst"),
        }).then(() => {
          redirect("/actions/create-text")
        })
        return
      }
      if (form.questionType === "-1" || form.questionType === undefined) {
        Toast.fire({
          icon: "error",
          title: t("selectQuestionTypeFirst"),
        }).then(() => {
          redirect("/actions/create-text")
        })
        return
      }

      if (form.number < 0) {
        Toast.fire({
          icon: "error",
          title: t("introduceLimitFirst"),
        }).then(() => {
          redirect("/actions/create-text")
        })
        return
      }
      if (form.number > max) {
        Toast.fire({
          icon: "error",
          title: t("questionsLimitExceeded"),
        }).then(() => {
          redirect("/actions/create-text")
        })
        return
      }
      const qaPairsResponse = await fetch(url, reqOptions)
      if (!qaPairsResponse.ok) {
        throw new Error("Network response was not ok")
      }
      const res1 = await qaPairsResponse.json()

      if (!res1 || !res1.data || !Array.isArray(res1.data)) {
        throw new Error("Invalid response format from API")
      }

      if (form.number == 0) {
        setQAPairs(res1.data)
        setIsChecked(new Array(res1.data.length).fill(false))
        setForm({ ...form, number: res1.data.length })
        return
      }
      const sliced = res1.data.slice(0, form.number)
      setQAPairs(sliced)
      setIsChecked(new Array(sliced.length).fill(false))
    } catch (error) {
      console.error(error)
      Toast.fire({
        icon: "error",
        title: t("unexpectedError"), // Or a more specific message like "Error generating questions"
        text: t("contentFormatError"),
      })
      setError(error.message)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleBloomsApi = async () => {
    const url = process.env.REACT_APP_BLOOMS_API_URL
    const levels = checkedLevels.toString()
    const data = {
      content: form.text, // form.type == "contextBased" ? form.text : form.topic,
      levels: levels,
      number: Number.parseInt(form.number, 10),
      difficulty: form.difficulty,
      language: form.language,
      key: process.env.REACT_APP_BLOOMS_API_KEY,
    }
    //console.log(form.language)
    // Token de Firebase para el gate de entitlement de IA (Fase 0).
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null
    const reqOptions = {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
    }

    try {
      setIsGenerating(true)
      if (form.difficulty === "-1" || form.difficulty === undefined) {
        Toast.fire({
          icon: "error",
          title: t("selectDifficultyFirst"),
        }).then(() => {
          redirect("/actions/create-text")
        })
        return
      }

      if (form.text === "") {
        Toast.fire({
          icon: "error",
          title: t("introduceTextFirst"),
        }).then(() => {
          redirect("/actions/create-text")
        })
        return
      }
      if (form.language === "") {
        Toast.fire({
          icon: "error",
          title: t("selectLanguageFirst"),
        }).then(() => {
          redirect("/actions/create-text")
        })
        return
      }
      if (form.number <= 0) {
        Toast.fire({
          icon: "error",
          title: t("introduceLimitFirst"),
        }).then(() => {
          redirect("/actions/create-text")
        })
        return
      }
      if (form.number > maxBlooms) {
        Toast.fire({
          icon: "error",
          title: t("questionsLimitExceeded"),
        }).then(() => {
          redirect("/actions/create-text")
        })
        return
      }
      const qaPairsResponse = await fetch(url, reqOptions)
      if (!qaPairsResponse.ok) {
        throw new Error("Network response was not ok")
      }
      const res1 = await qaPairsResponse.json()
      
      if (!res1 || !res1.data || !Array.isArray(res1.data)) {
        throw new Error("Invalid response format from API")
      }

      const ordered = res1.data.sort((a, b) => {
        const nivelA = levelsTexts.indexOf(a.level)
        const nivelB = levelsTexts.indexOf(b.level)

        return nivelA - nivelB
      })
      // La IA puede devolver preguntas de más. "number" es POR CADA nivel, así que
      // el total esperado = number × niveles seleccionados. Recortamos a ese tope.
      const perLevel = Number.parseInt(form.number, 10) || 0
      const numLevels = checkedLevels.filter(Boolean).length || 1
      const cap = perLevel > 0 ? perLevel * numLevels : ordered.length
      setQAPairs(ordered.slice(0, cap))
    } catch (error) {
      console.error(error)
      Toast.fire({
        icon: "error",
        title: t("unexpectedError"),
        text: t("contentFormatError"),
      })
      setError(error.message)
    } finally {
      setIsGenerating(false)
    }
  }

  const values = {
    limit: 2000000,
    title,
    form,
    handleChange,
    handleFileTextExtracted,
    handleReset,
    handleSubmit,
    id,
    handleGenerateQA,
    handleBloomsApi,
    qaPairs,
    isGenerating,
    handleCheck,
    handleCheckLevels,
    checked,
    handleDelete,
    isChecked,
    isCheckedLevels,
  }

  if (isLoading || loading) {
    return <Loading />
  }

  // Paywall: solo bloquea si el flag está activo (alineado con AI_GATE_MODE=enforce del backend).
  if (PAYWALL_ENFORCE && !entitlement.loading && !entitlement.ai) {
    return <AiPaywall />
  }

  return <CreateText values={values} />
}

export default CreateTextContainer
